import { useState, useCallback } from 'react';
import type { ToolPart } from '../../types';
import { useChatActions } from "../ChatActionsContext";

interface Props {
  part: ToolPart;
}

/**
 * Renders the `calculator` AG-UI tool. Toolspec lives on the backend in
 * `frontend_tools.py` and is fetched at runtime — see `QuestionCard.tsx`
 * for the rationale.
 */

type Op = '+' | '-' | '*' | '/' | null;

function compute(a: number, b: number, op: Op): number {
  switch (op) {
    case '+': return a + b;
    case '-': return a - b;
    case '*': return a * b;
    case '/': return b === 0 ? NaN : a / b;
    default: return b;
  }
}

function formatDisplay(value: string): string {
  if (value === 'Error') return value;
  if (value.length > 12) {
    const num = parseFloat(value);
    if (Number.isFinite(num)) return num.toPrecision(8).replace(/\.?0+$/, '');
  }
  return value;
}

export function CalculatorCard({ part }: Props) {
  const { sendToolResult } = useChatActions();
  const prompt = (part.state.input?.prompt as string | undefined) || '';
  const [display, setDisplay] = useState('0');
  const [accumulator, setAccumulator] = useState<number | null>(null);
  const [pendingOp, setPendingOp] = useState<Op>(null);
  const [resetOnNext, setResetOnNext] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  /**
   * Send the user's answer back as a canonical AG-UI `ToolMessage` in a
   * new POST /runs. No side-channel endpoint — the backend detects the
   * trailing tool result, rewrites Strands' conversation history, and
   * resumes the agent. `cancelled` returns an empty string so the LLM
   * can distinguish "user cancelled" from a real numeric answer.
   */
  const submit = useCallback(
    async (cancelled: boolean) => {
      setSubmitting(true);
      try {
        await sendToolResult(part.id, cancelled ? '' : display);
      } finally {
        setSubmitting(false);
      }
    },
    [part.id, display, sendToolResult],
  );

  // Finished — compact preview
  if (part.state.status === 'completed' || part.state.status === 'error') {
    return (
      <div className="tool-step tool-completed">
        <div className="tool-step-rail">
          <div className={`tool-step-dot ${part.state.status === 'error' ? 'tool-dot-error' : 'tool-dot-done'}`} />
        </div>
        <div className="tool-step-body">
          <div className="tool-step-label">Калькулятор — готово</div>
          {(part.state.output || part.state.error) && (
            <div className="question-preview">{part.state.output || part.state.error}</div>
          )}
        </div>
      </div>
    );
  }

  const inputDigit = (d: string) => {
    if (resetOnNext || display === '0' || display === 'Error') {
      setDisplay(d);
      setResetOnNext(false);
    } else {
      setDisplay(display + d);
    }
  };

  const inputDot = () => {
    if (resetOnNext) {
      setDisplay('0.');
      setResetOnNext(false);
    } else if (!display.includes('.')) {
      setDisplay(display + '.');
    }
  };

  const clear = () => {
    setDisplay('0');
    setAccumulator(null);
    setPendingOp(null);
    setResetOnNext(false);
  };

  const negate = () => {
    if (display === '0' || display === 'Error') return;
    setDisplay(display.startsWith('-') ? display.slice(1) : '-' + display);
  };

  const percent = () => {
    const n = parseFloat(display);
    if (Number.isFinite(n)) setDisplay(String(n / 100));
  };

  const applyOp = (nextOp: Op) => {
    const current = parseFloat(display);
    if (!Number.isFinite(current)) return;
    if (accumulator === null || pendingOp === null) {
      setAccumulator(current);
    } else if (!resetOnNext) {
      const result = compute(accumulator, current, pendingOp);
      if (!Number.isFinite(result)) {
        setDisplay('Error');
        setAccumulator(null);
        setPendingOp(null);
        setResetOnNext(true);
        return;
      }
      setAccumulator(result);
      setDisplay(formatDisplay(String(result)));
    }
    setPendingOp(nextOp);
    setResetOnNext(true);
  };

  const equals = () => {
    const current = parseFloat(display);
    if (accumulator === null || pendingOp === null || !Number.isFinite(current)) return;
    const result = compute(accumulator, current, pendingOp);
    setDisplay(Number.isFinite(result) ? formatDisplay(String(result)) : 'Error');
    setAccumulator(null);
    setPendingOp(null);
    setResetOnNext(true);
  };

  const Btn = ({
    label,
    onClick,
    variant,
    span,
  }: {
    label: string;
    onClick: () => void;
    variant?: 'fn' | 'op' | 'eq';
    span?: number;
  }) => (
    <button
      onClick={onClick}
      disabled={submitting}
      className={`calc-btn${variant ? ` calc-btn--${variant}` : ''}${span === 2 ? ' calc-btn--span2' : ''}`}
    >
      {label}
    </button>
  );

  return (
    <div className="calc-card">
      <div className="calc-header">
        <span className="calc-title">Калькулятор</span>
        <button className="calc-cancel" onClick={() => submit(true)} disabled={submitting}>
          Отмена
        </button>
      </div>
      {prompt && <div className="calc-prompt">{prompt}</div>}
      <div className="calc-display">
        {formatDisplay(display)}
        {pendingOp && accumulator !== null && (
          <div className="calc-display-op">{accumulator} {pendingOp}</div>
        )}
      </div>
      <div className="calc-grid">
        <Btn label="C" onClick={clear} variant="fn" />
        <Btn label="±" onClick={negate} variant="fn" />
        <Btn label="%" onClick={percent} variant="fn" />
        <Btn label="÷" onClick={() => applyOp('/')} variant="op" />

        <Btn label="7" onClick={() => inputDigit('7')} />
        <Btn label="8" onClick={() => inputDigit('8')} />
        <Btn label="9" onClick={() => inputDigit('9')} />
        <Btn label="×" onClick={() => applyOp('*')} variant="op" />

        <Btn label="4" onClick={() => inputDigit('4')} />
        <Btn label="5" onClick={() => inputDigit('5')} />
        <Btn label="6" onClick={() => inputDigit('6')} />
        <Btn label="−" onClick={() => applyOp('-')} variant="op" />

        <Btn label="1" onClick={() => inputDigit('1')} />
        <Btn label="2" onClick={() => inputDigit('2')} />
        <Btn label="3" onClick={() => inputDigit('3')} />
        <Btn label="+" onClick={() => applyOp('+')} variant="op" />

        <Btn label="0" onClick={() => inputDigit('0')} span={2} />
        <Btn label="." onClick={inputDot} />
        <Btn label="=" onClick={equals} variant="eq" />
      </div>
      <button
        className="calc-submit"
        onClick={() => submit(false)}
        disabled={submitting || display === 'Error'}
      >
        Отправить {display}
      </button>
    </div>
  );
}
